// Cave84 — InlineCombobox (redesign)
// Search-or-create combobox used across the Add Bottle screen for wine, producer
// and appellation. Presentational: the parent owns the text + selection state and
// decides what "create new" reveals inline (no navigation, no nested modal).
//
// Behaviour:
//  - Typing filters `options`; up to `maxResults` are shown.
//  - When the text is non-empty and no option matches it exactly, a "create new"
//    row appears at the bottom of the dropdown.
//  - Picking an option calls onPick; the create row calls onCreate.
//  - A selected value shows a check + clear affordance.

import { useEffect, useRef, useState } from 'react';

export interface ComboOption {
  id: string;
  label: string;
  sublabel?: string;
  badge?: string;
}

interface InlineComboboxProps {
  value: string;
  onValueChange: (text: string) => void;
  options: ComboOption[];
  selectedId: string | null;
  onPick: (option: ComboOption) => void;
  onClear: () => void;
  onCreate: () => void;
  createLabel: string;
  placeholder?: string;
  initialFocus?: boolean;
  maxResults?: number;
}

export function InlineCombobox({
  value,
  onValueChange,
  options,
  selectedId,
  onPick,
  onClear,
  onCreate,
  createLabel,
  placeholder,
  initialFocus,
  maxResults = 8,
}: InlineComboboxProps) {
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialFocus) inputRef.current?.focus();
  }, [initialFocus]);

  const term = value.trim().toLowerCase();
  const shown = options.slice(0, maxResults);
  const hasExactMatch = options.some(o => o.label.trim().toLowerCase() === term);
  const showCreate = term.length > 0 && !hasExactMatch && !selectedId;
  const showList = open && !selectedId && (shown.length > 0 || showCreate);

  const openNow = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={e => { onValueChange(e.target.value); openNow(); }}
          onFocus={openNow}
          onBlur={closeSoon}
          className={`w-full ps-3 pe-8 py-2 rounded-md border bg-surface-1 text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors ${
            selectedId ? 'border-gold' : 'border-border-medium'
          }`}
        />
        {selectedId ? (
          <button
            type="button"
            onClick={() => { onClear(); openNow(); }}
            aria-label="Clear selection"
            className="absolute end-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
            className="absolute end-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" />
          </svg>
        )}
      </div>

      {showList && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border-medium bg-surface-1 shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
          {shown.map(option => (
            <button
              key={option.id}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onPick(option); setOpen(false); }}
              className="w-full text-start flex items-center justify-between gap-3 px-3 py-2 hover:bg-surface-2 border-b border-border-subtle last:border-0 transition-colors"
            >
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-text-primary truncate">{option.label}</span>
                {option.sublabel && (
                  <span className="block text-xs text-text-muted truncate">{option.sublabel}</span>
                )}
              </span>
              {option.badge && (
                <span className="flex-shrink-0 text-[11px] text-gold border border-gold/50 rounded px-1.5 py-0.5">
                  {option.badge}
                </span>
              )}
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onCreate(); setOpen(false); }}
              className="w-full text-start flex items-center gap-2 px-3 py-2.5 bg-surface-3 hover:bg-surface-2 border-t border-border-subtle transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-gold flex-shrink-0">
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span className="text-sm text-gold truncate">
                {createLabel} — <span className="text-text-primary">"{value.trim()}"</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
